'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { useEffect, type RefObject } from 'react';

type Advance = (api: EmblaCarouselType) => void;

const advanceOne: Advance = api => { api.scrollNext(); };

/** One clock per carousel, using public engine events, never synthetic DOM clicks. */
export function installCarouselAutoplay(
  api: EmblaCarouselType,
  interactionRoot: HTMLElement,
  intervalMs: number,
  advance: Advance = advanceOne,
) {
  const viewport = api.rootNode();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let timer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  let visible = typeof IntersectionObserver === 'undefined';
  let dragging = false;
  let moving = false;
  let hovering = false;
  let focused = interactionRoot.contains(document.activeElement);

  const clear = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  const canRun = () => !disposed && visible && !document.hidden &&
    !reducedMotion.matches && !dragging && !moving && !hovering && !focused;
  const schedule = () => {
    clear();
    if (!canRun() || !api.canScrollNext()) return;
    timer = setTimeout(() => {
      timer = undefined;
      // Also guard a callback already queued before pointerDown/visibilitychange.
      if (!canRun() || !api.canScrollNext()) return;
      advance(api);
      // select/scroll pauses the clock synchronously; settle restarts a full interval.
      // A no-op scroll (e.g. too few slides) must not leave an orphan timer.
      schedule();
    }, intervalMs);
  };
  const onPointerDown = () => { dragging = true; clear(); };
  const onPointerUp = () => { dragging = false; schedule(); };
  const onMotionStart = () => {
    if (moving) return;
    moving = true;
    clear();
  };
  const onSettle = () => { moving = false; schedule(); };
  const onReInit = () => { dragging = false; moving = false; schedule(); };
  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    hovering = true;
    clear();
  };
  const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    hovering = false;
    schedule();
  };
  const onFocusIn = () => { focused = true; clear(); };
  const onFocusOut = (event: FocusEvent) => {
    focused = interactionRoot.contains(event.relatedTarget as Node | null);
    schedule();
  };
  const observer = typeof IntersectionObserver === 'undefined' ? undefined :
    new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      schedule();
    });
  observer?.observe(viewport);

  api.on('pointerDown', onPointerDown);
  api.on('pointerUp', onPointerUp);
  api.on('select', onMotionStart);
  api.on('scroll', onMotionStart);
  api.on('settle', onSettle);
  api.on('reInit', onReInit);
  interactionRoot.addEventListener('pointerenter', onPointerEnter);
  interactionRoot.addEventListener('pointerleave', onPointerLeave);
  interactionRoot.addEventListener('focusin', onFocusIn);
  interactionRoot.addEventListener('focusout', onFocusOut);
  interactionRoot.addEventListener('click', schedule);
  document.addEventListener('visibilitychange', schedule);
  reducedMotion.addEventListener('change', schedule);
  schedule();

  return () => {
    disposed = true;
    clear();
    observer?.disconnect();
    api.off('pointerDown', onPointerDown);
    api.off('pointerUp', onPointerUp);
    api.off('select', onMotionStart);
    api.off('scroll', onMotionStart);
    api.off('settle', onSettle);
    api.off('reInit', onReInit);
    interactionRoot.removeEventListener('pointerenter', onPointerEnter);
    interactionRoot.removeEventListener('pointerleave', onPointerLeave);
    interactionRoot.removeEventListener('focusin', onFocusIn);
    interactionRoot.removeEventListener('focusout', onFocusOut);
    interactionRoot.removeEventListener('click', schedule);
    document.removeEventListener('visibilitychange', schedule);
    reducedMotion.removeEventListener('change', schedule);
  };
}

export function useCarouselAutoplay(
  api: EmblaCarouselType | null | undefined,
  { enabled = true, intervalMs = 7000, interactionRootRef, advance = advanceOne }: {
    enabled?: boolean;
    intervalMs?: number;
    interactionRootRef?: RefObject<HTMLElement | null>;
    advance?: Advance;
  } = {},
) {
  useEffect(() => {
    if (!api || !enabled) return;
    return installCarouselAutoplay(api, interactionRootRef?.current ?? api.rootNode(), intervalMs, advance);
  }, [api, enabled, intervalMs, interactionRootRef, advance]);
}
